import { round2 } from "./invoiceFormat";

export function isGstRegistered(settings: Record<string, unknown>): boolean {
  return Boolean(settings?.gst_registered);
}

/** True if any business profile is GST-registered (AU multi-ABN rule). */
export function anyBusinessGstRegistered(
  businesses: Record<string, Record<string, unknown>> | null | undefined
): boolean {
  if (!businesses) return false;
  return Object.values(businesses).some((b) => isGstRegistered(b || {}));
}

export function parseGstRegisteredForm(value: unknown): boolean {
  const v = String(value || "")
    .trim()
    .toLowerCase();
  return v === "yes" || v === "true" || v === "1" || v === "on";
}

export function validateBusinessGstSettings(profile: Record<string, unknown>): string | null {
  const abn = String(profile?.business_abn || profile?.abn || "").replace(/\D/g, "");
  if (isGstRegistered(profile) && !abn) {
    return "ABN required when registered for GST.";
  }
  return null;
}

/** ATO: GST component of an inclusive price is price ÷ 11. */
export function unitExFromInc(inc: number): number {
  const n = round2(inc);
  return round2(n - round2(n / 11));
}

/** Derive the GST-inclusive unit from an ex-GST unit (×1.1). */
export function unitIncFromEx(ex: number): number {
  return round2(round2(ex) * 1.1);
}

export type LineAmountPreview = {
  unitEx: number;
  unitInc: number;
  amountEx: number;
  amountInc: number;
};

/**
 * Live preview for a draft line: customer-facing (inc) and ex-GST totals.
 * When not GST-applicable (or not registered), typed amount is used as-is for both.
 */
export function previewLineAmounts(opts: {
  amount: string;
  qty?: string;
  gstRegistered: boolean;
  gstApplicable: boolean;
  amountIncludesGst: boolean;
}): LineAmountPreview | null {
  const amtRaw = String(opts.amount || "").trim();
  if (!amtRaw) return null;
  const cleaned = amtRaw.replace(/\$/g, "").replace(/,/g, "");
  const entered = Number(cleaned);
  if (!Number.isFinite(entered) || entered < 0) return null;
  const unitEntered = round2(entered);
  let qty = 1;
  const qtyRaw = String(opts.qty || "").trim();
  if (qtyRaw) {
    const q = Number(qtyRaw);
    if (!Number.isFinite(q) || q <= 0) return null;
    qty = q;
  }
  const useInc =
    opts.gstRegistered && opts.gstApplicable && Boolean(opts.amountIncludesGst);
  let unitEx: number;
  let unitInc: number;
  if (!opts.gstRegistered || !opts.gstApplicable) {
    unitEx = unitEntered;
    unitInc = unitEntered;
  } else if (useInc) {
    unitInc = unitEntered;
    unitEx = unitExFromInc(unitEntered);
  } else {
    unitEx = unitEntered;
    unitInc = unitIncFromEx(unitEntered);
  }
  return {
    unitEx,
    unitInc,
    amountEx: round2(qty * unitEx),
    amountInc: round2(qty * unitInc),
  };
}

export type ParseLineItemRow = {
  description?: string;
  amount?: string;
  qty?: string;
  gst_free?: boolean;
  /** When true and line is GST-applicable, `amount` is GST-inclusive. */
  amount_includes_gst?: boolean;
};

export function parseLineItems(rows: Array<ParseLineItemRow>, gstRegistered = true) {
  const items: Array<Record<string, unknown>> = [];
  let rowNum = 0;
  for (const row of rows) {
    const desc = String(row.description || "").trim();
    const amtRaw = String(row.amount || "").trim();
    const qtyRaw = String(row.qty || "").trim();
    if (!desc && !amtRaw && !qtyRaw) continue;
    rowNum += 1;
    if (!desc) throw new Error(`Item ${rowNum}: add a description.`);
    const entered = parseAmountField(amtRaw, rowNum);
    const qty = parseQtyField(qtyRaw, rowNum);
    const gstApplicable = gstRegistered && !row.gst_free;
    const amountIncludesGst = Boolean(gstApplicable && row.amount_includes_gst);
    const unit = amountIncludesGst ? unitExFromInc(entered) : entered;
    const lineTotal = round2(qty * unit);
    items.push({
      description: desc,
      quantity: qty,
      unit_amount_ex_gst: unit,
      amount_ex_gst: lineTotal,
      gst_applicable: gstApplicable,
      amount_includes_gst: amountIncludesGst,
    });
  }
  if (!items.length) throw new Error("Add at least one line item with a description and amount.");
  const subtotal = round2(items.reduce((s, i) => s + Number(i.amount_ex_gst), 0));
  if (gstRegistered) {
    const taxable = round2(
      items.filter((i) => i.gst_applicable).reduce((s, i) => s + Number(i.amount_ex_gst), 0)
    );
    const gstFree = round2(
      items.filter((i) => !i.gst_applicable).reduce((s, i) => s + Number(i.amount_ex_gst), 0)
    );
    const gstAmount = round2(taxable * 0.1);
    const totalIncGst = round2(taxable + gstFree + gstAmount);
    return { items, subtotal, gstAmount, totalIncGst, taxableExGst: taxable, gstFreeExGst: gstFree };
  }
  return {
    items,
    subtotal,
    gstAmount: 0,
    totalIncGst: subtotal,
    taxableExGst: 0,
    gstFreeExGst: subtotal,
  };
}

function parseAmountField(raw: string, rowNum: number): number {
  const cleaned = raw.replace(/\$/g, "").replace(/,/g, "");
  if (!cleaned) throw new Error(`Item ${rowNum}: enter a valid amount.`);
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0) throw new Error(`Item ${rowNum}: enter a valid amount.`);
  return round2(value);
}

function parseQtyField(raw: string, rowNum: number): number {
  if (!raw) return 1;
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) throw new Error(`Item ${rowNum}: enter a valid quantity.`);
  return value;
}

export function lineItemsSummary(invoice: Record<string, unknown>): string {
  const items = invoice.line_items as Array<Record<string, unknown>> | undefined;
  if (items?.length) {
    const descs = items.map((i) => String(i.description || "").trim()).filter(Boolean);
    if (!descs.length) return String(invoice.description || "");
    if (descs.length === 1) return descs[0];
    if (descs.length === 2) return `${descs[0]} and ${descs[1]}`;
    if (descs.length === 3) return `${descs[0]}, ${descs[1]}, and ${descs[2]}`;
    return `${descs[0]}, ${descs[1]}, and ${descs.length - 2} more`;
  }
  return String(invoice.description || "");
}
